// Dashboard-eval validation pass. The `(dashboard …)` op is V-shape:
// tile specs carry *unresolved* `ColumnRef`s and eval does no I/O, so
// data-shape violations (a metric `:value` that resolves to a vector
// instead of a scalar) are invisible until a tile is rendered. This
// module resolves the scalar-bearing refs through the *same*
// `projectColumnRef` the render path uses (so the `session.fetch`
// cache is shared — no double CDF round-trip) and throws a typed
// `ExprError`, so callers can validate a dashboard *before* claiming
// success and feed the failure back for self-correction.

import { formatUnit } from '../../../units/registry.ts';
import type { SourcePos } from '../../ast.ts';
import { projectColumnRef } from '../../column-ref.ts';
import {
  AllNullColumnError,
  type CompositionTileKind,
  DuplicateSeriesError,
  EmptyCompositionError,
  type EmptyCompositionReason,
  InsufficientXError,
  InvertedBandError,
  MetricScalarError,
  MixedUnitAxisError,
  MixedUnitSeriesError,
  MultiDatasetTileError,
  NegativeCompositionError,
  NonNumericValueError,
  NonUniqueSeriesXError,
  SeriesXKindMismatchError,
  SingleSliceDonutError,
} from '../../errors.ts';
import {
  type Cell,
  type ColumnRef,
  cellScalar,
  type LazyDataset,
  type MaterializedDataset,
  unitOfCell,
} from '../../value.ts';
import type { AxisSide, SeriesType } from './enums.ts';
import {
  type CartesianSpec,
  type DashboardSpec,
  type DonutSpec,
  type MetricSpec,
  type TableSpec,
  TILE_KIND_LABELS,
  type TileSpec,
  type TreemapSpec,
} from './value.ts';

/**
 * SSOT scalar-cardinality predicate for a `(metric …)` tile. Pure, no
 * I/O — takes the already-resolved `:value` cells. Returns the typed
 * error to throw / display, or `null` when the metric is a valid
 * single scalar. A 0-row `:value` is permitted (an empty aggregate
 * renders as "no value"); only >1 rows is a misconfigured ref.
 *
 * Both consumers read this so the rule and its wording live in exactly
 * one place: `validateDashboardSpec` (execute-time, throws it) and
 * `MetricPanel` (render-time, shows `.message` as a graceful card).
 */
export function metricScalarError(spec: MetricSpec, cells: readonly Cell[], pos: SourcePos): MetricScalarError | null {
  return cells.length > 1 ? new MetricScalarError(spec.label, cells.length, pos) : null;
}

/**
 * Every `(col …)` ref a tile displays, keyed by tile kind so adding a
 * tile kind is a compile error until its refs are listed. SSOT for
 * "which columns does this tile read". Markdown carries no refs.
 */
const TILE_REFS: {
  readonly [KindType in TileSpec['kind']]: (tile: Extract<TileSpec, { kind: KindType }>) => readonly ColumnRef[];
} = {
  'metric-spec': (t) => [t.value, t.delta, t.asOf].filter((r): r is ColumnRef => r !== undefined),
  'cartesian-spec': (t) => [
    ...t.series.flatMap((s) => [s.x, s.y, s.z].filter((r): r is ColumnRef => r !== undefined)),
    ...t.bands.flatMap((b) => [b.x, b.lower, b.upper]),
  ],
  'table-spec': (t) => t.columns.map((c) => c.ref),
  'donut-spec': (t) => [t.category, t.value, t.center].filter((r): r is ColumnRef => r !== undefined),
  'treemap-spec': (t) => [t.label, t.value, t.group].filter((r): r is ColumnRef => r !== undefined),
  'markdown-spec': () => [],
};

/** All `(col …)` refs a tile reads, via the {@link TILE_REFS} SSOT. */
const tileRefs = (tile: TileSpec): readonly ColumnRef[] =>
  (TILE_REFS[tile.kind] as (t: TileSpec) => readonly ColumnRef[])(tile);

/**
 * SSOT single-dataset guard. A tile renders ONE row-aligned dataset (the
 * `MaterializedDataset` invariant makes its columns equal-length and
 * row-for-row aligned). Columns drawn from different bindings have no
 * shared row order, so combining them in a tile silently mispairs rows.
 * Returns the typed error to throw when a tile's `(col …)` refs span 2+
 * source datasets — grouped by binding name with each dataset's columns —
 * or `null`. Pure/structural: inspects only the spec, no resolution.
 */
export function multiDatasetTileError(tile: TileSpec, pos: SourcePos): MultiDatasetTileError | null {
  // Group columns by the binding they were read from (`ColumnRef.source`,
  // set by `col` from the head symbol). A ref with no source (an
  // expression head) carries no binding name and cannot prove a split, so
  // it is skipped — the guard fires only on 2+ distinct NAMED datasets.
  const named = tileRefs(tile).filter(
    (ref): ref is ColumnRef & { readonly source: string } => ref.source !== undefined,
  );
  const byDataset = named.reduce<Map<string, string[]>>(
    (acc, ref) => acc.set(ref.source, [...(acc.get(ref.source) ?? []), columnRefLabel(ref)]),
    new Map(),
  );
  if (byDataset.size <= 1) {
    return null;
  }
  return new MultiDatasetTileError(TILE_KIND_LABELS[tile.kind], [...byDataset.entries()], pos);
}

/**
 * SSOT structural predicate for `(cartesian …)` series. Pure, no I/O —
 * inspects only the V-shape `:y` `ColumnRef`s. Returns the typed error
 * to throw when two or more series map to the same column (same dataset
 * + column + struct path), or `null` when every series owns a distinct
 * column. A shared y-column makes `CartesianPanel` collapse all sharing
 * series onto one identical line (it keys merged points by x,
 * last-write-wins), so this is always a misconfigured spec.
 */
export function duplicateSeriesError(spec: CartesianSpec, pos: SourcePos): DuplicateSeriesError | null {
  // A tile is single-dataset (guaranteed by `multiDatasetTileError`
  // upstream), so two series collide exactly when they share a column +
  // struct path. The JSON key is collision-proof regardless of what
  // characters a column name or struct-path segment contains.
  const keyOf = (ref: ColumnRef): string => JSON.stringify([ref.column, ref.path]);
  const refs = spec.series.map((s) => ({ key: keyOf(s.y), label: columnRefLabel(s.y) }));
  const duplicates = [
    ...new Set(refs.filter((r, i) => refs.findIndex((o) => o.key === r.key) !== i).map((r) => r.label)),
  ];
  return duplicates.length === 0 ? null : new DuplicateSeriesError(duplicates, pos);
}

/**
 * A single dashboard ref slated for display, paired with a
 * human-readable `subject` for error messages. `metric` is set only for
 * `(metric …)` `:value` refs, which additionally run the scalar
 * cardinality predicate.
 */
type DisplayedRef = {
  readonly ref: ColumnRef;
  readonly subject: string;
  readonly metric?: MetricSpec;
};

/**
 * SSOT all-null predicate for any displayed dashboard ref (table column,
 * chart series channel, or metric value). Pure, no I/O — takes the
 * already-resolved cells. Returns the typed error when rows exist but
 * EVERY record is null (the signature of a ref that never resolved — a
 * direct-relation struct read for an unhydrated `name`, an unresolved
 * handle), or `null` otherwise. Partial nulls pass; a 0-row resolve
 * passes (a legitimately empty result, distinct from "has rows, all
 * null" — note `[].every(...)` is vacuously true, hence the length
 * guard).
 *
 * `validateDashboardSpec` throws it at execute time so the agent
 * re-queries before claiming success. Keeping the rule and its wording in
 * this one predicate (not inline) leaves a single seam to also drive a
 * render-time card later, mirroring `metricScalarError`.
 */
export function allNullRefError(subject: string, cells: readonly Cell[], pos: SourcePos): AllNullColumnError | null {
  return cells.length > 0 && cells.every((c) => c == null) ? new AllNullColumnError(subject, cells.length, pos) : null;
}
/** Display label for a column ref — `column`, or `column.path…` for a struct read. */
const columnRefLabel = (ref: ColumnRef): string =>
  ref.path.length === 0 ? ref.column : `${ref.column}.${ref.path.join('.')}`;

/**
 * The cartesian x-kinds whose `:x` data must be numeric, paired with the
 * leaf-type name the recovery message uses. `category` is absent — a
 * discrete axis legitimately carries numeric labels (years, bins), so a
 * numeric `:x` is never a type error there.
 */
const NUMERIC_X_KIND_LEAF: Partial<Record<CartesianSpec['xKind'], string>> = {
  numeric: 'number',
  time: 'timestamp',
};

/** The first cell that is present (non-null) yet not a number, or `undefined`. */
const firstNonNumericCell = (cells: readonly Cell[]): Cell | undefined =>
  cells.find((c) => {
    const scalar = cellScalar(c);
    return scalar !== null && typeof scalar !== 'number';
  });

/**
 * Classify a resolved channel by the data it carries: `numeric` (every
 * present cell is a number), `string` (at least one present cell is not),
 * or `empty` (no present cells — all-null or 0-row, handled by the
 * all-null guard). Drives the swap / kind-mismatch recovery in
 * {@link seriesAxisError}.
 */
export function classifyChannel(cells: readonly Cell[]): 'numeric' | 'string' | 'empty' {
  if (cells.every((c) => cellScalar(c) === null)) {
    return 'empty';
  }
  return firstNonNumericCell(cells) === undefined ? 'numeric' : 'string';
}

/**
 * SSOT type predicate for a `(cartesian …)` value channel that has no
 * category counterpart — scatter `:z`, band `:lower` / `:upper`. Pure, no
 * I/O — takes the already-resolved cells and an optional caller-computed
 * `recovery`. Returns the typed error when any non-null cell is
 * non-numeric, or `null` when every present cell is a number. Partial
 * nulls pass; all-null is left to `allNullRefError`. Series `:x` / `:y`
 * use the richer {@link seriesAxisError} instead (it can diagnose the
 * swap).
 */
export function nonNumericValueError(
  subject: string,
  cells: readonly Cell[],
  pos: SourcePos,
  recovery?: string,
): NonNumericValueError | null {
  const offending = firstNonNumericCell(cells);
  if (offending === undefined) {
    return null;
  }
  return new NonNumericValueError(
    subject,
    typeof cellScalar(offending) === 'string' ? 'string' : 'non-numeric',
    pos,
    recovery,
  );
}

/**
 * SSOT type guard for one cartesian series' `:x` / `:y` channels against
 * the tile's `:x-kind`. Pure, no I/O — takes the resolved cells and each
 * channel's column label. Classifies both channels and returns the first
 * typed error carrying column-specific recovery:
 *
 *  - a text `:y` value channel is unplottable; when `:x` is numeric the
 *    bindings are diagnosed as swapped (bind `:x` to the named category
 *    and `:y` to the named measure), otherwise the agent is told to pick
 *    a numeric measure;
 *  - a text `:x` on a `numeric` / `time` axis (with a well-typed `:y`) is
 *    a mislabelled kind — the recovery points at `:x-kind category`.
 *
 * `category` is never strict (numeric labels are legitimate); returns
 * `null` when both channels are well typed.
 */
export function seriesAxisError(
  series: {
    id: string;
    label: string;
    xColumn: string;
    yColumn: string;
    x: readonly Cell[];
    y: readonly Cell[];
  },
  xKind: CartesianSpec['xKind'],
  pos: SourcePos,
): NonNumericValueError | SeriesXKindMismatchError | null {
  const xType = classifyChannel(series.x);
  const yType = classifyChannel(series.y);

  if (yType === 'string') {
    const recovery =
      xType === 'numeric'
        ? `\`:y\` is bound to \`${series.yColumn}\` (text) while \`:x\` is \`${series.xColumn}\` (numeric) — the channels are swapped. Bind \`:x\` to the category \`${series.yColumn}\` and \`:y\` to the numeric measure \`${series.xColumn}\`.`
        : `\`:y\` must be a numeric measure, but \`${series.yColumn}\` holds text. Bind \`:y\` to a numeric column, or aggregate the data to produce one.`;
    return new NonNumericValueError(`series \`${series.label}\` y-value`, 'string', pos, recovery);
  }

  const expectedLeaf = NUMERIC_X_KIND_LEAF[xKind];
  if (expectedLeaf !== undefined && xType === 'string') {
    const expectedNoun = expectedLeaf === 'timestamp' ? 'timestamps' : 'numbers';
    const expectedColumn = expectedLeaf === 'timestamp' ? 'timestamp' : 'numeric';
    const recovery = `\`:x\` is bound to \`${series.xColumn}\`, which holds discrete text labels, not ${expectedNoun}. Set \`:x-kind category\` to plot it as a labelled axis, or bind \`:x\` to a ${expectedColumn} column.`;
    return new SeriesXKindMismatchError(series.id, xKind, expectedLeaf, 'string', pos, recovery);
  }

  return null;
}
/**
 * The plottable x keys of a resolved `:x` channel: every non-null
 * scalar, stringified the way the renderer keys points. SSOT shared by
 * the x-uniqueness and degenerate-x guards; null x is skipped (an
 * unplotted point).
 */
export function seriesXKeys(cells: readonly Cell[]): readonly string[] {
  return cells.flatMap((c) => {
    const v = cellScalar(c);
    return v === null ? [] : [String(v)];
  });
}

/**
 * SSOT guard for one cartesian series' `:x` uniqueness. Pure, no I/O —
 * inspects the already-resolved x cells and the series type. A `line` /
 * `area` / `bar` series is a function of x: `CartesianPanel` merges its
 * points keyed by x with last-write-wins, so two datapoints at one x
 * silently collapse to an arbitrary survivor (the long-form-not-aggregated
 * bug). Returns the typed error naming the offending `:x` / `:y` columns, a
 * sample of the colliding x values and the total collision count, or `null`
 * when every non-null x is unique. `scatter` is exempt — a point cloud is
 * many y per x by design; null x is ignored (an unplotted point).
 */
export function nonUniqueSeriesXError(
  series: { type: SeriesType; label: string; xColumn: string; yColumn: string; x: readonly Cell[] },
  pos: SourcePos,
): NonUniqueSeriesXError | null {
  if (series.type === 'scatter') {
    return null;
  }
  const counts = seriesXKeys(series.x).reduce<Map<string, number>>(
    (acc, key) => acc.set(key, (acc.get(key) ?? 0) + 1),
    new Map(),
  );
  const dupes = [...counts.entries()].filter(([, n]) => n > 1);
  if (dupes.length === 0) {
    return null;
  }
  const collisions = dupes.reduce((sum, [, n]) => sum + n, 0);
  const sample = dupes.slice(0, 3).map(([key]) => key);
  return new NonUniqueSeriesXError(series.label, series.xColumn, series.yColumn, sample, collisions, pos);
}

/**
 * The distinct unit symbols carried by a resolved value channel — one
 * entry per distinct boxed unit, empty for an entirely unitless
 * channel. SSOT for unit extraction shared by the per-series and
 * per-axis unit guards; plain (unboxed) numbers contribute nothing
 * (the engine's documented unit-drop case).
 */
export function channelUnitSymbols(cells: readonly Cell[]): readonly string[] {
  return [
    ...new Set(
      cells.flatMap((c) => {
        const unit = unitOfCell(c);
        return unit === undefined ? [] : [formatUnit(unit)];
      }),
    ),
  ];
}

/**
 * SSOT unit guard for one cartesian series' `:y` values. Pure, no I/O —
 * takes the already-resolved cells. A series whose unit-boxed y-cells
 * carry two or more DISTINCT units plots incomparable magnitudes on one
 * value axis, so it fails with the conflicting symbols named. A
 * unit-homogeneous or entirely unitless series passes; plain numbers
 * alongside boxed ones don't conflict (the engine's documented
 * unit-drop case).
 */
export function mixedUnitSeriesError(
  series: { label: string; yColumn: string; y: readonly Cell[] },
  pos: SourcePos,
): MixedUnitSeriesError | null {
  const symbols = channelUnitSymbols(series.y);
  if (symbols.length < 2) {
    return null;
  }
  return new MixedUnitSeriesError(series.label, series.yColumn, symbols, pos);
}

/**
 * SSOT unit guard across the series sharing one y-axis. Pure, no I/O —
 * takes each series' already-resolved `:y` cells and `:axis` binding.
 * Two series whose unit-homogeneous values carry DIFFERENT units cannot
 * share a value axis (one magnitude scale), so the first conflicting
 * side fails with each series' unit named. Unitless series never
 * conflict, and a series with internally mixed units is skipped — the
 * per-series guard already rejects it with a better diagnosis.
 */
export function mixedUnitAxisError(
  series: readonly { label: string; axis: AxisSide; y: readonly Cell[] }[],
  pos: SourcePos,
): MixedUnitAxisError | null {
  const boxed = series.flatMap((s) => {
    const [symbol, ...rest] = channelUnitSymbols(s.y);
    return symbol === undefined || rest.length > 0 ? [] : [{ label: s.label, axis: s.axis, symbol }];
  });
  const conflict = [...new Set(boxed.map((s) => s.axis))]
    .map((side) => {
      const members = boxed.filter((s) => s.axis === side);
      const distinct = new Set(members.map((m) => m.symbol));
      if (distinct.size < 2) {
        return null;
      }
      return new MixedUnitAxisError(
        side,
        members.map(({ label, symbol }) => ({ label, symbol })),
        pos,
      );
    })
    .find((e) => e !== null);
  return conflict ?? null;
}

/**
 * SSOT degenerate-x guard for a `(cartesian …)` tile. Pure, no I/O —
 * counts each series' distinct non-null `:x` values from the resolved
 * cells. A cartesian chart draws change across x; when NO series has at
 * least two distinct x values (e.g. a monthly query over a 30-day
 * window resolving to one bucket) the tile renders a single dot or bar
 * per series and conveys nothing an x-axis is for. One dense series
 * alongside sparse ones passes — sparse-vs-dense is a valid comparison.
 */
export function insufficientXError(
  series: readonly { label: string; xColumn: string; x: readonly Cell[] }[],
  pos: SourcePos,
): InsufficientXError | null {
  const counts = series.map((s) => ({
    series: s.label,
    xColumn: s.xColumn,
    distinct: new Set(seriesXKeys(s.x)).size,
  }));
  if (counts.length === 0 || counts.some((c) => c.distinct >= 2)) {
    return null;
  }
  return new InsufficientXError(counts, pos);
}

/**
 * SSOT bound-order guard for one `(band …)`. Pure, no I/O — compares
 * the resolved `:lower` / `:upper` cells row-wise. A row whose lower
 * bound exceeds its upper renders the band inside-out (or collapses
 * it), so any such row fails with samples and the total count; rows
 * where either bound is non-numeric are skipped (the numeric-value
 * guard owns those).
 */
export function invertedBandError(
  band: { name: string; lower: readonly Cell[]; upper: readonly Cell[] },
  pos: SourcePos,
): InvertedBandError | null {
  const inverted = band.lower.flatMap((cell, r) => {
    const lower = cellScalar(cell);
    const upper = cellScalar(band.upper[r] ?? null);
    return typeof lower === 'number' && typeof upper === 'number' && lower > upper ? [{ lower, upper }] : [];
  });
  if (inverted.length === 0) {
    return null;
  }
  const sample = inverted.slice(0, 3).map((i) => `${i.lower} > ${i.upper}`);
  return new InvertedBandError(band.name, sample, inverted.length, pos);
}

/** One drawable slice of a `(donut …)`: the category's display name and
 * its numeric magnitude. */
export type DonutSlice = { readonly name: string; readonly value: number };

/** Error-message subject for a `(donut …)` tile — the centre caption
 * when authored, else a generic stand-in. SSOT for the template. */
const donutSubject = (spec: DonutSpec): string => `donut \`${spec.centerLabel ?? 'slices'}\``;
/**
 * The slices a `(donut …)` can actually draw: one per row whose
 * category coerces to a non-null scalar AND whose value coerces to a
 * number; every other row is dropped. SSOT for the row-wise slice
 * predicate — `DonutPanel` builds its rendered slices through this same
 * function, so a donut that validates draws exactly these slices.
 */
export function donutSlices(categoryCells: readonly Cell[], valueCells: readonly Cell[]): readonly DonutSlice[] {
  return categoryCells
    .map((catCell, r): DonutSlice | null => {
      const cat = cellScalar(catCell);
      const val = cellScalar(valueCells[r]);
      if (cat === null || typeof val !== 'number') {
        return null;
      }
      return { name: String(cat), value: val };
    })
    .filter((s): s is DonutSlice => s !== null);
}

/** Shared empty/zero arithmetic for composition tiles: `'no-slices'`
 * when nothing survived the tile's row predicate, `'zero-total'` when
 * the survivors sum to 0, `null` when drawable. Unlike the all-null
 * guard, a 0-row resolve IS an error here: a composition tile renders
 * an empty result as confident-looking chrome (a 0-centre ring, a
 * blank canvas) rather than an obvious blank. */
const emptyCompositionReason = (values: readonly number[]): EmptyCompositionReason | null => {
  if (values.length === 0) {
    return 'no-slices';
  }
  const total = values.reduce((sum, v) => sum + v, 0);
  return total === 0 ? 'zero-total' : null;
};

/**
 * SSOT empty-donut predicate for a `(donut …)` tile. Pure, no I/O —
 * takes the already-resolved category and value cells, builds the
 * drawable slices via {@link donutSlices}, and returns the typed error
 * when nothing remains to draw or the drawable slices sum to 0. `null`
 * when at least one slice exists and the total is non-zero.
 */
export function emptyDonutError(
  spec: DonutSpec,
  categoryCells: readonly Cell[],
  valueCells: readonly Cell[],
  pos: SourcePos,
): EmptyCompositionError | null {
  const reason = emptyCompositionReason(donutSlices(categoryCells, valueCells).map((s) => s.value));
  return reason === null ? null : new EmptyCompositionError('donut', donutSubject(spec), reason, pos);
}
/**
 * SSOT single-slice predicate for a `(donut …)` tile. Pure, no I/O —
 * builds the drawable slices via {@link donutSlices} and fails when
 * exactly one remains: a one-slice donut is a 100% ring, composition
 * chrome around a single number a `(metric …)` states directly.
 */
export function singleSliceDonutError(
  spec: DonutSpec,
  categoryCells: readonly Cell[],
  valueCells: readonly Cell[],
  pos: SourcePos,
): SingleSliceDonutError | null {
  const [only, ...rest] = donutSlices(categoryCells, valueCells);
  if (only === undefined || rest.length > 0) {
    return null;
  }
  return new SingleSliceDonutError(donutSubject(spec), only.name, pos);
}

/**
 * The row predicate a treemap leaf must pass: a finite numeric value.
 * SSOT shared with `TreemapPanel` — which, unlike the donut, KEEPS rows
 * with a null label (rendered unlabelled) — so a treemap that validates
 * draws exactly the surviving rows.
 */
export const isDrawableTreemapValue = (v: number | string | null): v is number =>
  typeof v === 'number' && Number.isFinite(v);

/**
 * SSOT empty-treemap predicate for a `(treemap …)` tile. Pure, no I/O —
 * keeps the value cells that pass {@link isDrawableTreemapValue} and
 * returns the typed error when none survive or they sum to 0 (a blank
 * canvas). Only the value column matters: a null label never drops a
 * row.
 */
export function emptyTreemapError(valueCells: readonly Cell[], pos: SourcePos): EmptyCompositionError | null {
  const values = valueCells.map((c) => cellScalar(c)).filter(isDrawableTreemapValue);
  const reason = emptyCompositionReason(values);
  return reason === null ? null : new EmptyCompositionError('treemap', 'treemap', reason, pos);
}
/**
 * SSOT sign guard for a composition tile's drawable values. Pure, no
 * I/O — takes the rows the tile would draw (name paired with numeric
 * value). A donut slice or treemap leaf is a share of a non-negative
 * whole; a negative value has no such geometry, so any negative row
 * fails with samples and the total count.
 */
export function negativeCompositionError(
  tile: CompositionTileKind,
  subject: string,
  entries: readonly { name: string | null; value: number }[],
  pos: SourcePos,
): NegativeCompositionError | null {
  const negative = entries.filter((e) => e.value < 0);
  if (negative.length === 0) {
    return null;
  }
  const sample = negative.slice(0, 3).map((e) => (e.name === null ? `${e.value}` : `\`${e.name}\` = ${e.value}`));
  return new NegativeCompositionError(tile, subject, sample, negative.length, pos);
}

/**
 * Resolve every tile's displayed refs and throw the first data-shape
 * violation found: `(metric …)` `:value` cardinality (resolution-time),
 * `(cartesian …)` series uniqueness (pure/structural), an all-null guard
 * across every displayed table column, chart series/band channel and
 * metric value (rows exist but every record is null), a per-series
 * `:x`/`:y` type guard (a text value channel, or a text `:x` on a
 * `numeric` / `time` axis — the swapped-binding bug, reported with
 * column-specific recovery), a per-series mixed-unit guard on `:y` (boxed
 * cells carrying conflicting units are incomparable on one value axis),
 * a cross-series unit guard per y-axis (two unit-homogeneous series in
 * different units cannot share one magnitude scale), a degenerate-x
 * guard (a tile where no series has two distinct x values renders one
 * dot/bar per series), a numeric-value guard on the remaining value
 * channels (scatter `:z`, band `:lower` / `:upper`), a row-wise band
 * bound-order guard (lower > upper renders inside-out), and the
 * composition guards for `(donut …)` / `(treemap …)` tiles: negative
 * drawable values (no geometry for a negative share), emptiness (with no
 * drawable rows or an all-zero total they render as confident-looking
 * chrome — a 0-centre ring, a blank canvas — so they throw instead), and
 * the single-slice donut (a 100% ring composes nothing).
 * Partial nulls and a 0-row resolve are permitted.
 *
 * Mirrors `useResolvedColumns`'s resolution exactly (`projectColumnRef`
 * with the caller-bound `materialize`), so a tile that validates here
 * renders without a second fetch.
 */
export async function validateDashboardSpec(
  spec: DashboardSpec,
  materialize: (ds: LazyDataset) => Promise<MaterializedDataset>,
  pos: SourcePos,
): Promise<void> {
  // Structural guards run first (no ref resolution) so a misconfigured
  // spec fails fast before any dataset is materialised. The
  // single-dataset guard runs ahead of the duplicate-series check: a tile
  // spanning datasets is the more fundamental error, and clearing it lets
  // every downstream guard assume one row-aligned dataset.
  const datasetErr = spec.tiles
    .map((tile) => multiDatasetTileError(tile, pos))
    .find((e): e is MultiDatasetTileError => e !== null);
  if (datasetErr) {
    throw datasetErr;
  }

  const cartesianTiles = spec.tiles.filter((t): t is CartesianSpec => t.kind === 'cartesian-spec');
  const seriesErr = cartesianTiles
    .map((tile) => duplicateSeriesError(tile, pos))
    .find((e): e is DuplicateSeriesError => e !== null);
  if (seriesErr) {
    throw seriesErr;
  }

  // Every ref that feeds a displayed metric value / table column, tagged
  // with a human-readable subject (and, for metrics, the spec the
  // cardinality predicate needs). Cartesian refs resolve through the
  // structured pass below instead — the type guards need each series
  // keyed by id, channel role, column label and the tile's `:x-kind`.
  const metricRefs: DisplayedRef[] = spec.tiles
    .filter((t): t is MetricSpec => t.kind === 'metric-spec')
    .map((t) => ({ ref: t.value, subject: `metric \`${t.label}\``, metric: t }));
  const tableRefs: DisplayedRef[] = spec.tiles
    .filter((t): t is TableSpec => t.kind === 'table-spec')
    .flatMap((t) => t.columns.map((c) => ({ ref: c.ref, subject: `table column \`${c.label}\`` })));

  // Cartesian tiles resolve through a structured pass: the type guards
  // (per-series `:x`/`:y`, plus `:z` and band bounds) need each series
  // keyed by id, label, column labels and channel cells, plus the tile's
  // `:x-kind`, and the same cells feed the shared all-null pass below.
  // `:z` (bubble size) joins the value-type guard but not the all-null
  // pass — an all-null bubble channel renders unsized points, not a blank
  // chart.
  const cartesianResolved = await Promise.all(
    cartesianTiles.map(async (tile) => ({
      xKind: tile.xKind,
      series: await Promise.all(
        tile.series.map(async (s) => ({
          id: s.id,
          type: s.type,
          label: s.label,
          axis: s.axis,
          xColumn: columnRefLabel(s.x),
          yColumn: columnRefLabel(s.y),
          x: (await projectColumnRef(s.x, pos, materialize)).cells,
          y: (await projectColumnRef(s.y, pos, materialize)).cells,
          z: s.z === undefined ? null : (await projectColumnRef(s.z, pos, materialize)).cells,
        })),
      ),
      bands: await Promise.all(
        tile.bands.map(async (b, i) => ({
          name: b.label ?? `#${i + 1}`,
          x: (await projectColumnRef(b.x, pos, materialize)).cells,
          lower: (await projectColumnRef(b.lower, pos, materialize)).cells,
          upper: (await projectColumnRef(b.upper, pos, materialize)).cells,
        })),
      ),
    })),
  );

  // Composition tiles resolve apart from the flat per-ref collection —
  // the donut slice predicate is row-wise over category and value
  // together, and both tile kinds need their cells again for the
  // empty-composition guard; the cells still feed the shared all-null
  // pass below.
  const donutResolved = await Promise.all(
    spec.tiles
      .filter((t): t is DonutSpec => t.kind === 'donut-spec')
      .map(async (t) => ({
        spec: t,
        category: {
          subject: `${donutSubject(t)} category`,
          cells: (await projectColumnRef(t.category, pos, materialize)).cells,
        },
        value: {
          subject: `${donutSubject(t)} value`,
          cells: (await projectColumnRef(t.value, pos, materialize)).cells,
        },
      })),
  );
  const treemapResolved = await Promise.all(
    spec.tiles
      .filter((t): t is TreemapSpec => t.kind === 'treemap-spec')
      .map(async (t) => ({
        label: { subject: 'treemap label', cells: (await projectColumnRef(t.label, pos, materialize)).cells },
        value: { subject: 'treemap value', cells: (await projectColumnRef(t.value, pos, materialize)).cells },
      })),
  );

  // Resolve each flat (metric / table) ref once — shared with render via
  // the materialize cache, so a tile that validates here renders without
  // a second fetch.
  const resolved = await Promise.all(
    [...metricRefs, ...tableRefs].map(async (d) => ({
      ...d,
      cells: (await projectColumnRef(d.ref, pos, materialize)).cells,
    })),
  );

  // Metric cardinality is the more specific failure (a >1-row `:value`
  // is misconfigured regardless of null-ness), so check it before the
  // all-null guard on the same resolved cells.
  const cardErr = resolved
    .map((r) => (r.metric ? metricScalarError(r.metric, r.cells, pos) : null))
    .find((e): e is MetricScalarError => e !== null);
  if (cardErr) {
    throw cardErr;
  }

  // All-null is the more specific failure (the signature of a ref that
  // never resolved), so the cartesian channels, donut and treemap columns
  // join this pass before the type and composition guards run.
  const cartesianNullRefs = cartesianResolved.flatMap((t) => [
    ...t.series.flatMap((s) => [
      { subject: `series \`${s.label}\` x-axis`, cells: s.x },
      { subject: `series \`${s.label}\` y-value`, cells: s.y },
    ]),
    ...t.bands.flatMap((b) => [
      { subject: `band \`${b.name}\` x-axis`, cells: b.x },
      { subject: `band \`${b.name}\` lower bound`, cells: b.lower },
      { subject: `band \`${b.name}\` upper bound`, cells: b.upper },
    ]),
  ]);
  const nullErr = [
    ...resolved,
    ...cartesianNullRefs,
    ...donutResolved.flatMap((d) => [d.category, d.value]),
    ...treemapResolved.flatMap((t) => [t.label, t.value]),
  ]
    .map((r) => allNullRefError(r.subject, r.cells, pos))
    .find((e): e is AllNullColumnError => e !== null);
  if (nullErr) {
    throw nullErr;
  }

  // A series whose `:x` / `:y` channels are mis-typed — a text value
  // channel (the swapped-binding bug) or a text `:x` on a numeric/time
  // axis — fails with column-specific recovery instead of rendering a
  // blank plot.
  const seriesTypeErr = cartesianResolved
    .flatMap((t) => t.series.map((s) => seriesAxisError(s, t.xKind, pos)))
    .find((e): e is NonNumericValueError | SeriesXKindMismatchError => e != null);
  if (seriesTypeErr) {
    throw seriesTypeErr;
  }

  // A series whose y-values carry conflicting per-row units plots
  // incomparable magnitudes on one value axis — reject with the
  // conflicting symbols named. Runs before the x-uniqueness guard: mixed
  // units are the more fundamental diagnosis of the same un-filtered
  // long-form data, and the unit symbols point straight at the fix.
  const seriesUnitErr = cartesianResolved
    .flatMap((t) => t.series.map((s) => mixedUnitSeriesError(s, pos)))
    .find((e): e is MixedUnitSeriesError => e !== null);
  if (seriesUnitErr) {
    throw seriesUnitErr;
  }

  // Series that are individually unit-homogeneous can still disagree
  // with each other: two units on one y-axis share one magnitude scale.
  // Runs after the per-series guard so an internally mixed series gets
  // the more specific diagnosis first.
  const axisUnitErr = cartesianResolved
    .map((t) => mixedUnitAxisError(t.series, pos))
    .find((e): e is MixedUnitAxisError => e !== null);
  if (axisUnitErr) {
    throw axisUnitErr;
  }

  // A line/area/bar series with two datapoints at the same x is
  // un-aggregated long-form data — the renderer merges points by x
  // last-write-wins and plots an arbitrary survivor, so reject it here
  // with reshape recovery instead. Scatter (a point cloud) is exempt.
  const seriesXErr = cartesianResolved
    .flatMap((t) => t.series.map((s) => nonUniqueSeriesXError(s, pos)))
    .find((e): e is NonUniqueSeriesXError => e !== null);
  if (seriesXErr) {
    throw seriesXErr;
  }

  // A tile where NO series resolves two distinct x values renders one
  // dot/bar per series — degenerate cartesian geometry, typically a
  // query whose granularity matches its window. A sparse series next to
  // a dense one passes: that comparison is valid.
  const xCountErr = cartesianResolved
    .map((t) => insufficientXError(t.series, pos))
    .find((e): e is InsufficientXError => e !== null);
  if (xCountErr) {
    throw xCountErr;
  }

  // The remaining value channels — scatter `:z` and band `:lower` /
  // `:upper` — have no category counterpart to swap with, so they carry a
  // direct "bind a numeric column" recovery.
  const channelTypeErr = cartesianResolved
    .flatMap((t) => [
      ...t.series.flatMap((s) =>
        s.z === null
          ? []
          : [
              nonNumericValueError(
                `series \`${s.label}\` :z (bubble size)`,
                s.z,
                pos,
                'Bind `:z` to a numeric column — scatter point area must be numeric.',
              ),
            ],
      ),
      ...t.bands.flatMap((b) => [
        nonNumericValueError(
          `band \`${b.name}\` lower bound`,
          b.lower,
          pos,
          "Bind the band's lower bound to a numeric column.",
        ),
        nonNumericValueError(
          `band \`${b.name}\` upper bound`,
          b.upper,
          pos,
          "Bind the band's upper bound to a numeric column.",
        ),
      ]),
    ])
    .find((e): e is NonNumericValueError => e != null);
  if (channelTypeErr) {
    throw channelTypeErr;
  }

  // Band bounds are numeric by now (the value-type guard above), so the
  // row-wise order check is meaningful: lower > upper renders the band
  // inside-out.
  const bandOrderErr = cartesianResolved
    .flatMap((t) => t.bands.map((b) => invertedBandError(b, pos)))
    .find((e): e is InvertedBandError => e !== null);
  if (bandOrderErr) {
    throw bandOrderErr;
  }

  // Negative drawable values are the more specific diagnosis of a bad
  // composition (a +5/−5 donut also sums to 0), so the sign guard runs
  // before the empty-composition guard on the same slices.
  const negativeErr = [
    ...donutResolved.map((d) =>
      negativeCompositionError('donut', donutSubject(d.spec), donutSlices(d.category.cells, d.value.cells), pos),
    ),
    ...treemapResolved.map((t) =>
      negativeCompositionError(
        'treemap',
        'treemap',
        t.value.cells.flatMap((c, r) => {
          const value = cellScalar(c);
          if (!isDrawableTreemapValue(value)) {
            return [];
          }
          const name = cellScalar(t.label.cells[r] ?? null);
          return [{ name: name === null ? null : String(name), value }];
        }),
        pos,
      ),
    ),
  ].find((e): e is NegativeCompositionError => e !== null);
  if (negativeErr) {
    throw negativeErr;
  }

  const compositionErr = [
    ...donutResolved.map((d) => emptyDonutError(d.spec, d.category.cells, d.value.cells, pos)),
    ...treemapResolved.map((t) => emptyTreemapError(t.value.cells, pos)),
  ].find((e): e is EmptyCompositionError => e !== null);
  if (compositionErr) {
    throw compositionErr;
  }

  // A donut that survives the emptiness guard can still be a 100% ring:
  // exactly one drawable slice composes nothing, so it fails toward a
  // `(metric …)` instead. Runs after the empty guard — a lone zero slice
  // is the zero-total diagnosis, not a single-slice one.
  const singleSliceErr = donutResolved
    .map((d) => singleSliceDonutError(d.spec, d.category.cells, d.value.cells, pos))
    .find((e): e is SingleSliceDonutError => e !== null);
  if (singleSliceErr) {
    throw singleSliceErr;
  }
}
